import { useQueryClient } from '@tanstack/react-query'
import z from 'zod'

import {
  getStaffRegistrationLabel,
  REGIONS_BY_ID,
  type RegionId,
  regionIdSchema,
  STAFF_ROLES,
  type StaffBookingVisibility,
  type StaffRole,
  staffBookingVisibilitySchema,
  staffRoleSchema,
} from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'

const BOOKING_VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: null, label: 'None' },
]

const VET_ROLES: string[] = ['vet', 'remote_vet'] satisfies StaffRole[]
const BOOKABLE_ROLES: string[] = ['vet', 'remote_vet'] satisfies StaffRole[]

const REGION_OPTIONS = Object.values(REGIONS_BY_ID).map((r) => ({
  value: r.id,
  label: `${r.flag} ${r.name}`,
}))

const staffSchema = z.object({
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  role: staffRoleSchema,
  isSuperuser: z.boolean(),
  licenseNumber: z.string().nullable(),
  bookingVisibility: staffBookingVisibilitySchema.nullable(),
  regions: z.array(regionIdSchema).min(1, 'Select at least one region'),
})

export interface StaffFormValues {
  email: string
  firstName: string
  lastName: string
  role: StaffRole
  isSuperuser: boolean
  licenseNumber: string | null
  bookingVisibility: StaffBookingVisibility | null
  regions: RegionId[]
}

type StaffFormProps = {
  defaultValues?: Partial<StaffFormValues>
  onSuccess?: () => void
  onCancel?: () => void
  staffId?: string
}

/**
 * Unified staff form for creating and editing staff members.
 * When `staffId` is provided the form operates in edit mode — the email field
 * is disabled and submission PATCHes the existing record. Otherwise it POSTs a
 * new staff member.
 */
export function StaffForm({ defaultValues, staffId, onSuccess, onCancel }: StaffFormProps) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  const isEditing = staffId !== undefined

  const form = useAppForm({
    defaultValues: {
      regions: ['uk'],
      licenseNumber: null,
      bookingVisibility: null,
      isSuperuser: false,
      ...defaultValues,
    } as StaffFormValues,
    validators: {
      onSubmit: staffSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        if (isEditing) {
          const response = await api.patch('/admin/staff/:id', {
            params: { id: staffId! },
            body: {
              firstName: value.firstName || undefined,
              lastName: value.lastName || undefined,
              role: value.role as StaffRole,
              isSuperuser: value.isSuperuser,
              licenseNumber: value.licenseNumber,
              regions: value.regions,
              bookingVisibility: value.bookingVisibility,
            },
          })
          if (!response.success) throw new Error(response.error.message)
        } else {
          const response = await api.post('/admin/staff', {
            body: {
              email: value.email,
              firstName: value.firstName || undefined,
              lastName: value.lastName || undefined,
              role: value.role as StaffRole,
              isSuperuser: value.isSuperuser,
              licenseNumber: value.licenseNumber || null,
              regions: value.regions,
              bookingVisibility: value.bookingVisibility,
            },
          })
          if (!response.success) throw new Error(response.error.message)
        }
        queryClient.invalidateQueries({ queryKey: ['admin', 'staff'] })
        queryClient.invalidateQueries({ queryKey: ['staff'] })
        if (!isEditing) form.reset()
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save staff member',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <form.AppField name="email">
            {(field) => (
              <field.InputField
                label="Email"
                type="email"
                placeholder="vet@clinic.com"
                disabled={isEditing}
              />
            )}
          </form.AppField>

          <div className="flex gap-3">
            <form.AppField name="firstName">
              {(field) => (
                <field.InputField label="First name" placeholder="Jane" layoutClassName="flex-1" />
              )}
            </form.AppField>
            <form.AppField name="lastName">
              {(field) => (
                <field.InputField label="Last name" placeholder="Smith" layoutClassName="flex-1" />
              )}
            </form.AppField>
          </div>

          <form.AppField
            name="role"
            listeners={{
              onChange: ({ value }) => {
                form.setFieldValue('isSuperuser', value === 'admin')
                if (!VET_ROLES.includes(value)) {
                  form.setFieldValue('licenseNumber', null)
                }
                if (!BOOKABLE_ROLES.includes(value)) {
                  form.setFieldValue('bookingVisibility', null)
                } else {
                  form.setFieldValue('bookingVisibility', 'public')
                }
              },
            }}
          >
            {(field) => (
              <field.SelectField label="Role" placeholder="Select a role" items={STAFF_ROLES} />
            )}
          </form.AppField>

          <form.AppField name="isSuperuser">
            {(field) => (
              <field.CheckboxField
                label="Superuser"
                description="Manage staff, archive members and impersonate members."
              />
            )}
          </form.AppField>

          <form.AppField name="regions">
            {(field) => (
              <field.CheckboxGroupField
                label="Regions"
                required
                options={REGION_OPTIONS}
                orientation="horizontal"
              />
            )}
          </form.AppField>

          <form.Subscribe selector={(s) => ({ role: s.values.role, regions: s.values.regions })}>
            {({ role, regions }) =>
              VET_ROLES.includes(role) && (
                <form.AppField name="licenseNumber">
                  {(field) => (
                    <field.InputField
                      label={getStaffRegistrationLabel(regions)}
                      placeholder="1234567"
                    />
                  )}
                </form.AppField>
              )
            }
          </form.Subscribe>

          <form.Subscribe selector={(s) => s.values.role}>
            {(role) =>
              BOOKABLE_ROLES.includes(role) && (
                <form.AppField name="bookingVisibility">
                  {(field) => (
                    <field.SelectField
                      label="Booking visibility"
                      placeholder="Select visibility"
                      items={BOOKING_VISIBILITY_OPTIONS}
                    />
                  )}
                </form.AppField>
              )
            }
          </form.Subscribe>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton />
        </div>
      </form.AppForm>
    </form>
  )
}
